import { useEffect, useState } from 'react';
import { api, ApiError } from '../../api/client.js';
import LoadingState from '../LoadingState.jsx';
import EmptyState from '../EmptyState.jsx';
import KpiCard from '../KpiCard.jsx';
import WindowPicker from './WindowPicker.jsx';

/**
 * What callers asked for, and what we actually answered.
 *
 * Under oRTB 2.x `BidRequest.cur` is the set of currencies the caller will accept, and a bid
 * outside it is non-compliant -- a strict exchange discards it silently. Until the 2026-09-30
 * currency work this service answered INR unconditionally and nothing read the caller's
 * declaration; production ran at "USD requested / INR answered" on every instrumented auction.
 *
 * That is fixed, and this tab is how it stays fixed. The report also carries the tmax and bidfloor
 * each caller actually sends, which is the only place an operator can see the inbound contract
 * without reading auction_events by hand.
 */

const int = (n) => (n === null || n === undefined ? '—' : Number(n).toLocaleString());
const money = (n) => (n === null || n === undefined ? '—' : Number(n).toFixed(4));
const pct = (n) => (n === null || n === undefined ? '—' : `${(n * 100).toFixed(4)}%`);

export default function InboundContractTab() {
  const [days, setDays] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .inboundContract(days)
      .then((res) => !cancelled && setData(res))
      .catch((err) => !cancelled && setError(err instanceof ApiError ? err.message : 'Could not load the inbound contract report.'))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [days]);

  const groups = data?.groups || [];
  const s = data?.summary || {};
  const mismatchedPairs = s.mismatchedPairs || [];

  return (
    <div className="stack">
      <section className="card">
        <div className="card__header">
          <h2 className="card__title">Inbound contract</h2>
          <WindowPicker days={days} onChange={setDays} disabled={loading} />
        </div>
        <p className="card__hint">
          The currency, timeout and floor each caller declares, against what BidDigi answered. A bid
          returned in a currency the caller did not accept is non-compliant and may be discarded
          with no error shown to either side.
        </p>

        {loading && <LoadingState label="Loading inbound contract" />}
        {error && !loading && <EmptyState title="Couldn't load the inbound contract report" message={error} />}

        {!loading && !error && data && (
          <>
            <div className="kpi-grid" style={{ marginTop: '14px' }}>
              <KpiCard label="Inbound auctions" value={int(s.inboundAuctions)} tone="accent" />
              <KpiCard
                label="Answered in an unaccepted currency"
                value={int(s.auctionsAnsweredInAnUnacceptedCurrency)}
              />
              <KpiCard label="No contract recorded" value={int(s.auctionsWithNoContractRecorded)} />
              <KpiCard
                label="Currencies requested"
                value={(s.distinctRequestCurrencies || []).join(', ') || '—'}
              />
            </div>

            {mismatchedPairs.length > 0 && (
              <div className="form-error" style={{ marginTop: '14px' }}>
                <strong>Currency mismatch on live traffic:</strong> {mismatchedPairs.join('; ')}. A
                caller that does not accept the currency we answered in may be discarding these
                bids.
              </div>
            )}

            {groups.length === 0 ? (
              <EmptyState title="No inbound traffic" message="No inbound auction was recorded in this window." />
            ) : (
              <div className="table-scroll" style={{ marginTop: '18px' }}>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Requested cur</th>
                      <th>Answered cur</th>
                      <th>Floor cur</th>
                      <th className="num">Auctions</th>
                      <th className="num">With bid</th>
                      <th className="num">Bid rate</th>
                      <th className="num">tmax min / max</th>
                      <th className="num">Floor avg</th>
                      <th>Match</th>
                    </tr>
                  </thead>
                  <tbody>
                    {groups.map((g, i) => (
                      <tr key={i}>
                        <td><code className="code-chip">{g.requestCur || '—'}</code></td>
                        <td><code className="code-chip">{g.responseCur || '—'}</code></td>
                        <td><code className="code-chip">{g.bidfloorCur || '—'}</code></td>
                        <td className="num">{int(g.auctions)}</td>
                        <td className="num">{int(g.auctionsWithBid)}</td>
                        <td className="num">{pct(g.bidRate)}</td>
                        <td className="num cell-muted">{g.tmax ? `${g.tmax.min} / ${g.tmax.max}` : '—'}</td>
                        <td className="num cell-muted">{money(g.bidfloor?.avg)}</td>
                        <td>
                          {g.currencyMismatch === true ? (
                            <span className="status-badge status-badge--error">Mismatch</span>
                          ) : g.currencyMismatch === false ? (
                            <span className="status-badge status-badge--active">OK</span>
                          ) : (
                            <span className="status-badge status-badge--neutral">Unknown</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </section>
    </div>
  );
}
